/**
 * ContributionSchedulePage — the Contribution Schedule generator
 * (PRD_ContributionSchedule C1/C3/C10).
 *
 * Same shell as ContributionBasisReportPage: controls, Generate, then the
 * usePdfPreview + PdfPreviewPanel lifecycle — no on-screen table. The figures
 * come from one callable (computeContributionSchedule), which runs the payroll
 * calculation server-side; this page only sorts and prints them.
 */
import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../contexts/ToastContext';
import { todayManilaYMD } from '../../utils/periodDate';
import { defaultMonth } from '../../services/contributionBasisReportModel';
import {
  SORT_OPTIONS,
  DEFAULT_SORT,
  scheduleReminder,
  buildContributionScheduleModel,
} from '../../services/contributionScheduleModel';
import { longMonthLabel } from '../../constants/basisNotes';
import { loadContributionSchedulePdf, PDF_LOAD_ERROR } from '../../services/pdfLoader';
import { usePdfPreview } from '../../hooks/usePdfPreview';
import PdfPreviewPanel from './PdfPreviewPanel';

const inputStyle = {
  padding: '0.5rem 0.75rem',
  border: '1px solid var(--ui-border)',
  borderRadius: '6px',
  fontSize: '0.875rem',
  background: 'var(--ui-surface)',
  fontFamily: 'var(--font-family-primary)',
};

const labelStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.375rem',
  fontSize: '0.75rem',
  fontWeight: 600,
  color: 'var(--ui-text-secondary)',
};

const cardStyle = {
  padding: '1.25rem',
  background: 'var(--ui-surface)',
  border: '1px solid var(--ui-border)',
  borderRadius: '12px',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
};

const generatedStamp = (now) =>
  `${now.toLocaleDateString('en-CA', { timeZone: 'Asia/Manila' })} ${now.toLocaleTimeString('en-GB', {
    timeZone: 'Asia/Manila',
    hour: '2-digit',
    minute: '2-digit',
  })}`;

const ContributionSchedulePage = () => {
  const { company } = useAuth();
  const toast = useToast();

  // C3: only finished months — the previous Manila month is the ceiling.
  const maxMonth = defaultMonth([], todayManilaYMD());
  const [month, setMonth] = useState(maxMonth);
  const [sortKey, setSortKey] = useState(DEFAULT_SORT.key);
  const [sortDirection, setSortDirection] = useState(DEFAULT_SORT.direction);
  const [generating, setGenerating] = useState(false);
  const { preview, isMobile, showPreview, clearPreview } = usePdfPreview();

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      let pdf;
      try {
        pdf = await loadContributionSchedulePdf();
      } catch (err) {
        console.error('[ContributionSchedulePage] PDF chunk load failed:', err);
        clearPreview();
        toast.error(PDF_LOAD_ERROR);
        return;
      }

      let result;
      try {
        result = (await httpsCallable(functions, 'computeContributionSchedule')({ month })).data;
      } catch (err) {
        console.error('[ContributionSchedulePage] computeContributionSchedule failed:', err);
        clearPreview();
        toast.error('Failed to calculate contributions. Please try again.');
        return;
      }

      if (result.rows.length === 0) {
        clearPreview();
        toast.error(`No employees were employed in ${longMonthLabel(month)}.`);
        return;
      }
      const model = buildContributionScheduleModel({
        month,
        rows: result.rows,
        methods: result.methods,
        sort: { key: sortKey, direction: sortDirection },
      });
      showPreview(
        pdf.buildContributionSchedulePdf({
          companyName: company.name,
          month,
          generatedAt: generatedStamp(new Date()),
          model,
        }),
        pdf.contributionScheduleFileName({ month }),
        longMonthLabel(month)
      );
    } finally {
      setGenerating(false);
    }
  };

  const generateDisabled = generating || !month;

  return (
    <div>
      <h1 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--ui-primary)', margin: '0 0 0.25rem 0' }}>
        Contribution Schedule
      </h1>
      <p style={{ fontSize: '0.875rem', color: 'var(--ui-text-secondary)', margin: '0 0 1.5rem 0' }}>
        One month's SSS, PhilHealth and Pag-IBIG per employee — employer and employee shares, worked out from
        that month's attendance the way payroll works them out. Not a government filing.
      </p>

      <div style={cardStyle}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '1rem' }}>
          <label style={labelStyle}>
            MONTH
            <input
              type="month"
              value={month}
              max={maxMonth}
              onChange={(e) => setMonth(e.target.value)}
              style={inputStyle}
            />
          </label>

          <label style={labelStyle}>
            SORT BY
            <select value={sortKey} onChange={(e) => setSortKey(e.target.value)} style={inputStyle}>
              {SORT_OPTIONS.map((o) => (
                <option key={o.key} value={o.key}>{o.label}</option>
              ))}
            </select>
          </label>

          <label style={labelStyle}>
            ORDER
            <select value={sortDirection} onChange={(e) => setSortDirection(e.target.value)} style={inputStyle}>
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>
          </label>

          <button
            type="button"
            onClick={handleGenerate}
            disabled={generateDisabled}
            style={{
              padding: '0.625rem 1.25rem',
              background: 'var(--ui-primary)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '0.875rem',
              cursor: generateDisabled ? 'not-allowed' : 'pointer',
              opacity: generateDisabled ? 0.6 : 1,
              fontFamily: 'var(--font-family-primary)',
            }}
          >
            {generating ? 'Generating…' : 'Generate'}
          </button>
        </div>
        {month && (
          <p style={{ fontSize: '0.8125rem', color: 'var(--ui-text-secondary)', margin: '1rem 0 0 0' }}>
            {scheduleReminder(month)}
          </p>
        )}
      </div>

      <PdfPreviewPanel preview={preview} isMobile={isMobile} title="Contribution Schedule preview" />
    </div>
  );
};

export default ContributionSchedulePage;
